import React from "react";
import { MobileBottomSheetFrame } from "@shared/ui/overlay/mobile-bottom-sheet-frame";
import { OverlayDismissBridgeProvider, useOverlayDismissRequest } from "@shared/ui/overlay/overlay-dismiss-bridge";
import type { MobileBottomSheetShellConfig, MobileWindowShellField } from "./types";

function resolveShellField<TProps extends object>(
    value: MobileWindowShellField<TProps> | undefined,
    props: TProps,
): React.ReactNode | undefined {
    if (value == null) {
        return undefined;
    }

    return typeof value === "function" ? value(props) : value;
}

interface MobileBottomSheetShellFrameProps<TProps extends object> {
    Content: React.ComponentType<TProps>;
    shell: MobileBottomSheetShellConfig<TProps>;
    props: TProps;
}

function MobileBottomSheetShellFrame<TProps extends object>({
    Content,
    shell,
    props,
}: MobileBottomSheetShellFrameProps<TProps>) {
    const requestDismiss = useOverlayDismissRequest();

    return (
        <MobileBottomSheetFrame
            title={resolveShellField(shell.title, props)}
            supportingTitle={resolveShellField(shell.supportingTitle, props)}
            footer={resolveShellField(shell.footer, props)}
            className={shell.className}
            contentClassName={shell.contentClassName}
            footerClassName={shell.footerClassName}
            closeButtonLabel={shell.closeButtonLabel}
            onRequestClose={requestDismiss}
        >
            <Content {...props} />
        </MobileBottomSheetFrame>
    );
}

/**
 * Оборачивает content-компонент в bottom sheet shell для mobile overlay.
 */
export function withMobileBottomSheetShell<TProps extends object>(
    Content: React.ComponentType<TProps>,
    shell: MobileBottomSheetShellConfig<TProps>,
): React.ComponentType<TProps> {
    function MobileBottomSheetContent(props: TProps) {
        return (
            <OverlayDismissBridgeProvider>
                <MobileBottomSheetShellFrame Content={Content} shell={shell} props={props} />
            </OverlayDismissBridgeProvider>
        );
    }

    MobileBottomSheetContent.displayName = `MobileBottomSheet(${Content.displayName ?? Content.name ?? "Content"})`;

    return MobileBottomSheetContent;
}
